import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useOwnersQuery, useOwnersSummaryQuery } from '../../hooks';
import { isOwnerSortProperty, ownerPageSizes, OwnerSortDirection } from '../../model';
import { useOwnersRouteFilters } from '../../useOwnersRouteFilters';
import { createOwnersTableColumns } from './columns';
import { OwnersSummary } from './OwnersSummary';

export function OwnersTable() {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const routeFilters = useOwnersRouteFilters();
  const { filter } = routeFilters;
  const query = useOwnersQuery(filter);
  const summary = useOwnersSummaryQuery();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const canCreate = can(ShigoPermission.DemandPartnersCreate);
  const canEdit = can(ShigoPermission.DemandPartnersEdit);
  const columns = createOwnersTableColumns({ canEdit, navigate });

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <div className="mx-auto flex w-full max-w-[1920px] min-w-0 flex-col gap-6">
        <OwnersSummary summary={summary.data} loading={summary.isLoading} error={summary.isError} />
        <DashboardDataTable
          title={dashboardT('owners.title')}
          tableLabel={dashboardT('owners.tableLabel')}
          columns={columns}
          items={query.data?.content ?? []}
          getRowKey={(owner) => owner.id}
          loading={query.isLoading}
          loadingMessage={dashboardT('owners.loading')}
          errorMessage={query.isError ? dashboardT('owners.loadError') : undefined}
          emptyMessage={dashboardT('owners.empty')}
          minWidthClassName="min-w-[920px]"
          toolbar={
            <>
              <DashboardTableSearchInput
                aria-label={dashboardT('owners.searchLabel')}
                placeholder={dashboardT('owners.searchPlaceholder')}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
              {canCreate ? (
                <Button asChild>
                  <Link to="/demand/owners/create">
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('owners.new')}
                  </Link>
                </Button>
              ) : null}
            </>
          }
          pagination={{
            value: {
              page: filter.page,
              pageSize: filter.size,
              totalItems: query.data?.totalElements ?? 0,
            },
            pageSizes: ownerPageSizes,
            disabled: query.isFetching,
            onChange: (value) => {
              routeFilters.setPagination(value.page, value.pageSize);
            },
          }}
          sort={{
            value: filter.sortProperty
              ? {
                  key: filter.sortProperty,
                  direction:
                    filter.sortDirection === OwnerSortDirection.Asc
                      ? DashboardDataTableSortDirection.Asc
                      : DashboardDataTableSortDirection.Desc,
                }
              : undefined,
            onChange: (value) => {
              if (!value) {
                routeFilters.setSort(null, filter.sortDirection);
                return;
              }
              if (!isOwnerSortProperty(value.key)) return;
              routeFilters.setSort(
                value.key,
                value.direction === DashboardDataTableSortDirection.Asc
                  ? OwnerSortDirection.Asc
                  : OwnerSortDirection.Desc,
              );
            },
          }}
        />
      </div>
    </main>
  );
}
